package it.neckar.lizergy.model.configuration.quote.builder

import it.neckar.commons.tags.toTagsOrEmpty
import it.neckar.lizergy.model.company.PlannerCompanyInformation
import it.neckar.lizergy.model.configuration.PlannerConfiguration
import it.neckar.lizergy.model.configuration.components.EnWG14aIntegrationType
import it.neckar.lizergy.model.configuration.components.UeberspannungsschutzACType
import it.neckar.lizergy.model.configuration.moduleLayout.roof.ResolvedColoredMetalRoofTile
import it.neckar.lizergy.model.configuration.moduleLayout.roof.ResolvedColoredOnlyWhereVisibleMetalRoofTile
import it.neckar.lizergy.model.configuration.moduleLayout.roof.ResolvedNormalMetalRoofTile
import it.neckar.lizergy.model.financial.currency.ValueAddedTax
import it.neckar.lizergy.model.financial.quote.Amount
import it.neckar.lizergy.model.financial.quote.AmountUnit
import it.neckar.lizergy.model.financial.quote.QuoteElements
import it.neckar.lizergy.model.financial.quote.QuoteSectionBuilder
import it.neckar.lizergy.model.financial.quote.Visibility.Internal
import it.neckar.lizergy.model.financial.quote.Visibility.Public
import it.neckar.lizergy.model.price.PriceList
import it.neckar.lizergy.model.price.RoofIsolationPrices
import it.neckar.lizergy.model.price.lizergy.LizergyPriceListFactory
import it.neckar.open.collections.fastForEach
import it.neckar.open.formatting.format
import it.neckar.open.i18n.CurrentI18nConfiguration
import it.neckar.open.kotlin.lang.requireNotNull
import it.neckar.open.unit.other.pct
import it.neckar.open.unit.si.km

/**
 * Creates QuotePositions
 */
class LizergyQuotePositionsBuilder {
  val section1Photovoltaik: QuoteSectionBuilder = QuoteSectionBuilder("Photovoltaikanlage", Public)

  val section11KomplettAnlage: QuoteSectionBuilder = QuoteSectionBuilder("Komplettanlage inklusive Montage", Public).also {
    section1Photovoltaik.add(it)
  }

  val section2Battery: QuoteSectionBuilder = QuoteSectionBuilder("Batteriesystem der neuesten Generation", Public)

  val section3Additional: QuoteSectionBuilder = QuoteSectionBuilder("Zusätzliche Arbeiten", Public)

  private val builders: MutableList<QuoteSectionBuilder> = mutableListOf(
    section1Photovoltaik,
    section2Battery,
    section3Additional,
  )


  /**
   * Creates the quote
   */
  fun build(discount: @pct Double): QuoteElements {
    return QuoteElements(builders.map {
      it.build()
    }, discount)
  }

  fun buildQuoteElementsForConfiguration(configuration: PlannerConfiguration, totalDistanceTravelledForAssembly: @km Int, priceList: PriceList, sellingCompanyInformation: PlannerCompanyInformation, monitoringCompanyInformation: PlannerCompanyInformation): QuoteElements {

    with(configuration) {

      val sellingCompanyName = sellingCompanyInformation.simpleName
      val monitoringCompanyName = monitoringCompanyInformation.simpleName

      val inverterSelection = facilityConfiguration.inverterSelectionWithHybridInverter
      val batteryConfiguration = facilityConfiguration.batteryConfiguration

      val toAndBackTravelDistance = shippingDistance?.times(2.0) ?: 0.0

      //
      // Details for position 1
      //
      section11KomplettAnlage.details = buildString {
        modulesReport.countByModules.groupBy { moduleType -> moduleType.moduleType.warranty }.forEach { (warranty, entries) ->
          appendLine(
            entries.joinToString(", ") { (moduleType, count) ->
              "$count monokristalline Module ${moduleType.description}"
            }
          )

          if (entries.isNotEmpty()) {
            appendLine("mit je $warranty Jahren Herstellergarantie garantieren beste Verarbeitung und hohe Belastbarkeit.")
          }
        }

        val firstInverter = inverterSelection.firstOrNull()?.element
        val inverterManufacturerText = when (firstInverter?.manufacturer) {
          Manufacturer.SMA -> "des Marktführers SMA steht $sellingCompanyName für bedingungslose Qualität und hohen Ertrag."
          Manufacturer.IBC -> "inklusive 12 Jahre Herstellergarantie eines deutschen Lieferanten und Garantiegebers steht $sellingCompanyName für bedingungslose Qualität und hohen Ertrag."
          null -> null
          else -> throw NotImplementedError("Unknown inverter manufacturer: ${firstInverter.manufacturer}")
        }
        when {
          inverterSelection.size == 1 -> {
            val inverter = inverterSelection.first().element
            appendLine("Mit dem Wechselrichter ${inverter.description} $inverterManufacturerText")
          }

          inverterSelection.size > 1 -> {
            append("Mit den Wechselrichtern ")

            append(inverterSelection.joinToString(", ") { (inverterType, amount) ->
              when (amount) {
                1 -> {
                  inverterType.description
                }

                else -> {
                  "${amount}× ${inverterType.description}"
                }
              }
            })

            appendLine(" $inverterManufacturerText")
          }
        }

        appendLine("Für das Untergestell verwenden wir namhafte deutsche Hersteller.")

        if (numberOfModulesWithSchwarzeKlemmen > 0) {
          appendLine("$numberOfModulesWithSchwarzeKlemmen Module werden mit schwarzen Klemmen montiert.")
        }

        if (numberOfModulesKreuzverbund > 0) {
          appendLine("$numberOfModulesKreuzverbund Module werden im Kreuzverbund montiert.")
        }

        if (facilityConfiguration.numberOfOptimizers > 0) {
          appendLine("Die Anlage enthält ${facilityConfiguration.numberOfOptimizers} Moduloptimierer.")
        }

        appendLine("$sellingCompanyName Qualität heißt: Top Produkte, lange Garantiezeiten, Planung und Installation aus einer Hand und von Fachkräften ausgeführt.")
      }

      // Add the modules to section 1
      modulesReport.countByModules.forEach { (moduleType, count) ->
        section11KomplettAnlage {
          item(
            headline = moduleType.description,
            priceForOneElement = priceList[moduleType],
            amount = count.toDouble(),
            relevance = LizergyCalculationCategories.EconomicsCalculation,
            visibility = Internal,
            valueAddedTax = ValueAddedTax.zero,
          )
        }
      }

      if (inverterSelection.isNotEmpty()) {
        section11KomplettAnlage {
          section(headline = "Wechselrichter", visibility = Internal) {
            inverterSelection.fastForEach { (inverterType, amount) ->
              item(
                headline = inverterType.description,
                priceForOneElement = priceList[inverterType],
                amount = amount.toDouble(),
                relevance = LizergyCalculationCategories.EconomicsCalculation,
                visibility = Internal,
                valueAddedTax = ValueAddedTax.zero,
              )
            }
          }
        }
      }

      moduleLayouts.validElements.forEach { moduleLayout ->
        val roof = moduleLayout.roof
        val roofType = roof.roofType
        val modulesForThisRoof = moduleLayout.modulesCount

        //Create a section for each roof
        section11KomplettAnlage {
          section(headline = roof.roofName, visibility = Internal) {
            // Aufschlag für Dachtypen
            item(
              headline = "${roof.roofName}: Aufschlag ${roofType.description}",
              priceForOneElement = priceList.assemblyCostPerModule(roofType),
              amount = modulesForThisRoof.toDouble(),
              relevance = LizergyCalculationCategories.EconomicsCalculation,
              visibility = Internal,
              valueAddedTax = ValueAddedTax.zero,
            )

            if (roof.verstaerktesSchienensystem) {
              item(
                headline = "${roof.roofName}: Aufpreis verstärktes Schienensystem",
                priceForOneElement = priceList.aufpreisSchienensystemPrice,
                amount = modulesForThisRoof.toDouble(),
                relevance = LizergyCalculationCategories.EconomicsCalculation,
                visibility = Internal,
                valueAddedTax = ValueAddedTax.zero,
              )
            }

            if (roof.schwarzeKlemmen) {
              item(
                headline = "${roof.roofName}: Aufpreis schwarze Klemmen",
                priceForOneElement = priceList.aufpreisSchwarzeKlemmePrice,
                amount = modulesForThisRoof.toDouble(),
                relevance = LizergyCalculationCategories.EconomicsCalculation,
                visibility = Internal,
                valueAddedTax = ValueAddedTax.zero,
              )
            }

            roof.isolationThickness?.let {
              if (it > 0) {
                item(
                  headline = "${roof.roofName}: Aufschlag Aufdachdämmung ${it.format()} cm [Preiskategorie ${
                    RoofIsolationPrices.thicknessCategory(
                      it
                    )
                  }]",
                  priceForOneElement = priceList.roofIsolationCostPerModule(it),
                  amount = modulesForThisRoof.toDouble(),
                  relevance = LizergyCalculationCategories.EconomicsCalculation,
                  visibility = Internal,
                  valueAddedTax = ValueAddedTax.zero,
                )
              }
            }

          }
        }
      }

      // Initial setup costs
      section11KomplettAnlage {
        item(
          headline = "Baustelleneinrichtung",
          priceForOneElement = priceList.sitePreparation,
          relevance = LizergyCalculationCategories.EconomicsCalculation,
          visibility = Internal,
          valueAddedTax = ValueAddedTax.zero,
        )
      }


      // Fahrtkosten
      /**
       * Add the assembly travel costs
       */
      section11KomplettAnlage {
        item(
          headline = "Fahrtkosten Montage (km)",
          priceForOneElement = priceList.perKilometerTravelled,
          amount = totalDistanceTravelledForAssembly.toDouble(),
          relevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.Assembly,
          visibility = Internal,
          valueAddedTax = ValueAddedTax.zero,
        ) {
          amount = Amount(totalDistanceTravelledForAssembly, AmountUnit.KiloMeters)
        }
      }

      /**
       * The electrician always needs to drive at least once to and back
       */
      section11KomplettAnlage {
        item(
          headline = "Fahrtkosten Elektriker (km)",
          priceForOneElement = priceList.perKilometerTravelled,
          amount = toAndBackTravelDistance,
          relevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.ElectricityWork,
          visibility = Internal,
          valueAddedTax = ValueAddedTax.zero,
        ) {
          amount = Amount(toAndBackTravelDistance, AmountUnit.KiloMeters)
        }
      }

      // Adds the optimizers
      if (facilityConfiguration.numberOfOptimizers > 0) {
        section11KomplettAnlage {
          itemWithWork(
            headline = "Moduloptimierer",
            priceForMaterialAndWork = priceList.optimizerPrice,
            amount = facilityConfiguration.numberOfOptimizers,
            materialRelevance = LizergyCalculationCategories.EconomicsCalculation,
            workRelevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.Assembly,
            visibility = Internal,
            materialValueAddedTax = ValueAddedTax.zero,
            workValueAddedTax = ValueAddedTax.zero,
          )
        }
      }

      if (facilityConfiguration.kabelwegZugschlagLength > 0) {
        section11KomplettAnlage {
          itemWithWork(
            headline = "Zuschlag Kabelweg (m)",
            priceForMaterialAndWork = priceList.zuschlagKabelweg,
            amount = facilityConfiguration.kabelwegZugschlagLength,
            materialRelevance = LizergyCalculationCategories.EconomicsCalculation.toTagsOrEmpty(),
            workRelevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.Assembly,
            visibility = Internal,
            materialValueAddedTax = ValueAddedTax.zero,
            workValueAddedTax = ValueAddedTax.zero,
          )
        }
      }

      // Aufschlag Kreuzverbund
      numberOfModulesKreuzverbund.takeIf { it > 0 }?.let { numberOfModules ->
        if (numberOfModules > 0) {
          section11KomplettAnlage {
            itemWithWork(
              headline = "Aufschlag Kreuzverbund",
              priceForMaterialAndWork = priceList.aufschlagKreuzverbund,
              amount = numberOfModules,
              materialRelevance = LizergyCalculationCategories.EconomicsCalculation,
              workRelevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.Assembly,
              visibility = Internal,
              materialValueAddedTax = ValueAddedTax.zero,
              workValueAddedTax = ValueAddedTax.zero,
            )
          }
        }
      }

      val assemblyDifficulty = assemblyConfiguration.assemblyDifficulty
      val assemblyPricePerModule = priceList[assemblyDifficulty]
      section11KomplettAnlage {
        itemWithWork(
          headline = "Montage (inklusive Unterkonstruktion) ${assemblyDifficulty.description}",
          priceForMaterialAndWork = assemblyPricePerModule,
          amount = totalNumberOfModules,
          relevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.Assembly,
          visibility = Internal,
          valueAddedTax = ValueAddedTax.zero,
        )
      }

      val facilityWorkEffort = facilityConfiguration.facilityWorkEffort
      section11KomplettAnlage {
        item(
          headline = facilityWorkEffort.headline,
          priceForOneElement = facilityWorkEffort.priceForOneElement,
          amount = facilityWorkEffort.amount,
          relevance = facilityWorkEffort.relevance,
          visibility = Internal,
          valueAddedTax = facilityWorkEffort.valueAddedTax,
        ) {
          details = facilityWorkEffort.details
        }
      }


      assemblyConfiguration.assemblyStatus.let {

        if (it.dachhakenSetzen == true) {
          section1Photovoltaik {
            section(
              headline = "Rabatt Unterkonstruktion-Selbstmontage",
              visibility = Public,
            ) {
              val discount = priceList.optionalDiscountPrices.dachhakenSetzenDiscount
              itemWithWork(
                headline = "Rabatt Selbstmontage (pro Modul Arbeitszeit)",
                priceForMaterialAndWork = assemblyPricePerModule,
                relevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.Assembly,
                amount = totalNumberOfModules,
                factor = discount,
                visibility = Internal,
                valueAddedTax = ValueAddedTax.zero,
              )
            }
          }
        }

        if (it.schienenMontage == true) {
          section1Photovoltaik {
            section(
              headline = "Rabatt Schienen-Selbstmontage",
              visibility = Public,
            ) {
              val discount = priceList.optionalDiscountPrices.schienenMontageDiscount
              itemWithWork(
                headline = "Rabatt Selbstmontage (pro Modul Arbeitszeit)",
                priceForMaterialAndWork = assemblyPricePerModule,
                relevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.Assembly,
                amount = totalNumberOfModules,
                factor = discount,
                visibility = Internal,
                valueAddedTax = ValueAddedTax.zero,
              )
            }
          }
        }

        if (it.kabelZiehenUndModuleMontieren == true) {
          section1Photovoltaik {
            section1Photovoltaik {
              section(
                headline = "Rabatt Module Montieren-Selbstmontage",
                visibility = Public,
              ) {
                val discount = priceList.optionalDiscountPrices.kabelZiehenUndModuleMontierenDiscount
                itemWithWork(
                  headline = "Rabatt Selbstmontage (pro Modul Arbeitszeit)",
                  priceForMaterialAndWork = assemblyPricePerModule,
                  relevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.Assembly,
                  amount = totalNumberOfModules,
                  factor = discount,
                  visibility = Internal,
                  valueAddedTax = ValueAddedTax.zero,
                )
              }
            }
          }
        }

        if (it.dachhakenGestellt == true) {
          section1Photovoltaik {
            section(
              headline = "Rabatt Unterkonstruktion (ohne Schienen) wird gestellt",
              visibility = Public,
            ) {
              val discount = priceList.optionalDiscountPrices.dachhakenGestelltDiscount
              itemWithWork(
                headline = "Rabatt Selbstmontage (pro Modul Arbeitszeit)",
                priceForMaterialAndWork = assemblyPricePerModule,
                relevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.Assembly,
                amount = totalNumberOfModules,
                factor = discount,
                visibility = Internal,
                valueAddedTax = ValueAddedTax.zero,
              )

              val roofDiscount = priceList.optionalDiscountPrices.dachhakenGestelltRoofDiscount
              moduleLayouts.validElements.filter { moduleLayout ->
                val roofType = moduleLayout.roof.roofType
                roofType == LizergyPriceListFactory.ziegelDach || roofType == LizergyPriceListFactory.biberschwanzDach
              }.fastForEach { moduleLayout ->
                item(
                  headline = "Rabatt ${moduleLayout.roof.roofType.description} ${moduleLayout.layoutName} (pro Modul Arbeitszeit)",
                  priceForOneElement = priceList[moduleLayout.roof.roofType],
                  amount = moduleLayout.modulesCount.toDouble(),
                  factor = roofDiscount,
                  relevance = LizergyCalculationCategories.EconomicsCalculation,
                  visibility = Internal,
                  valueAddedTax = ValueAddedTax.zero,
                )
              }

            }

          }
        }

      }


      // Additional Positions under 1
      // 1.x Metal Roof Tiles
      val metalRoofTileEntries = metalTilesCalculator.getEntries()
      if (metalRoofTileEntries.isNotEmpty()) {
        section1Photovoltaik {
          section(
            headline = "Metalldachplatten",
            visibility = Public,
          ) {
            metalRoofTileEntries.forEach {
              val entry = it.value
              val metalRoofTileType = entry.matchingMetalTile

              section(
                headline = "${metalRoofTileType.description} für ${it.key.roofName}, ${it.key.roofType.description}",
                visibility = Public,
              ) {
                this.details = "Metallziegel für ${entry.totalModulesCount} Module"

                val coloredTilesAmount = when (val metalRoofTileConstructionType = entry.metalRoofTileConstructionType) {
                  null, ResolvedNormalMetalRoofTile -> 0.0
                  is ResolvedColoredMetalRoofTile -> entry.amount
                  is ResolvedColoredOnlyWhereVisibleMetalRoofTile -> metalRoofTileConstructionType.amount.toDouble()
                }
                val normalTilesAmount = entry.amount - coloredTilesAmount

                when (val metalRoofTileConstructionType = entry.metalRoofTileConstructionType) {

                  null -> {}

                  ResolvedNormalMetalRoofTile -> {
                    item(
                      headline = entry.descriptionWithoutColor,
                      priceForOneElement = priceList[metalRoofTileType],
                      amount = normalTilesAmount,
                      relevance = LizergyCalculationCategories.EconomicsCalculation,
                      visibility = Public,
                      valueAddedTax = ValueAddedTax.zero,
                    )
                  }

                  is ResolvedColoredMetalRoofTile -> {
                    item(
                      headline = entry.descriptionWithColor,
                      priceForOneElement = priceList[metalRoofTileType] + priceList.aufschlagColoredMetalPlatesPrices[metalRoofTileConstructionType.color],
                      amount = coloredTilesAmount,
                      relevance = LizergyCalculationCategories.EconomicsCalculation,
                      visibility = Public,
                      valueAddedTax = ValueAddedTax.zero,
                    )
                  }

                  is ResolvedColoredOnlyWhereVisibleMetalRoofTile -> {
                    item(
                      headline = entry.descriptionWithoutColor,
                      priceForOneElement = priceList[metalRoofTileType],
                      amount = normalTilesAmount,
                      relevance = LizergyCalculationCategories.EconomicsCalculation,
                      visibility = Public,
                      valueAddedTax = ValueAddedTax.zero,
                    )

                    item(
                      headline = "${entry.descriptionWithColor} im Sichtbereich",
                      priceForOneElement = priceList[metalRoofTileType] + priceList.aufschlagColoredMetalPlatesPrices[metalRoofTileConstructionType.color],
                      amount = coloredTilesAmount,
                      relevance = LizergyCalculationCategories.EconomicsCalculation,
                      visibility = Public,
                      valueAddedTax = ValueAddedTax.zero,
                    )
                  }

                }

              }

            }
          }
        }
      }

      // 1.x Gerüst
      section1Photovoltaik {
        section(
          headline = "Gerüst / Absturzsicherung",
          visibility = Public,
        ) {
          scaffoldingAreas().forEach {
            val moduleLayout = it.key?.let { moduleLayoutId -> moduleLayouts[moduleLayoutId] }

            itemWithWork(
              headline = buildString {
                append("Gerüst")
                moduleLayout?.let { append(" ${moduleLayout.layoutName}") }
              },
              priceForMaterialAndWork = priceList.scaffolding,
              amount = it.value,
              materialRelevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.Scaffolding,
              workRelevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.Scaffolding,
              visibility = Internal,
              materialValueAddedTax = ValueAddedTax.zero,
              workValueAddedTax = ValueAddedTax.zero,
            )
          }
        }
      }

      // 3.x Vogelschutzgitter
      if (assemblyConfiguration.vogelschutzgitterLength > 0) {
        section3Additional {
          itemWithWork(
            headline = "Vogelschutzgitter",
            priceForMaterialAndWork = priceList.vogelschutzgitter,
            amount = assemblyConfiguration.vogelschutzgitterLength,
            materialRelevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.Assembly,
            workRelevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.Assembly,
            visibility = Public,
            materialValueAddedTax = ValueAddedTax.nineteenPercent,
            workValueAddedTax = ValueAddedTax.nineteenPercent,
          ) {
            details = "für PV-Anlagen\nEffektiver Schutz vor Vögeln unter PV-Modulen. Das robuste, witterungsbeständige Schutzgitter verhindert das Eindringen und Nisten unter den PV-Modulen."
          }
        }
      }

      // 3.x Schneefang
      if (assemblyConfiguration.schneefangLength > 0) {
        section3Additional {
          itemWithWork(
            headline = "Schneefang",
            priceForMaterialAndWork = priceList.schneefang,
            amount = assemblyConfiguration.schneefangLength,
            materialRelevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.Assembly,
            workRelevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.Assembly,
            visibility = Public,
            materialValueAddedTax = ValueAddedTax.nineteenPercent,
            workValueAddedTax = ValueAddedTax.nineteenPercent,
          )
        }
      }

      // 3.x Marderschutz
      if (assemblyConfiguration.marderschutz) {
        val marderschutzDetails = "Verkleidung der Solarkabel durch ein zusätzliches Wellrohr zum Schutz vor Tierbissen und äußeren Einflüssen. Für mehr Sicherheit und eine längere Lebensdauer der PV-Kabel."

        if (totalNumberOfModules > 0) {
          section3Additional {
            itemWithWork(
              headline = "Marderschutz (pro Modul)",
              priceForMaterialAndWork = priceList.marderschutzProModul,
              amount = totalNumberOfModules,
              materialRelevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.Assembly,
              workRelevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.Assembly,
              visibility = Public,
              materialValueAddedTax = ValueAddedTax.nineteenPercent,
              workValueAddedTax = ValueAddedTax.nineteenPercent,
            ) {
              details = marderschutzDetails
            }
          }
        }

        if (facilityConfiguration.kabelwegZugschlagLength > 0) {
          section3Additional {
            itemWithWork(
              headline = "Marderschutz (pro Kabelweg)",
              priceForMaterialAndWork = priceList.marderschutzProKabelweg,
              amount = facilityConfiguration.kabelwegZugschlagLength,
              materialRelevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.Assembly,
              workRelevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.Assembly,
              visibility = Public,
              materialValueAddedTax = ValueAddedTax.nineteenPercent,
              workValueAddedTax = ValueAddedTax.nineteenPercent,
            ) {
              details = marderschutzDetails
            }
          }
        }
      }

      // 1.x Dachständer-Isolierung
      if (assemblyConfiguration.dachstaenderIsolierung) {
        section1Photovoltaik {
          itemWithWork(
            headline = "Dachständer-Isolierung",
            priceForMaterialAndWork = priceList.dachStaenderIsolierung,
            visibility = Public,
            materialRelevance = LizergyCalculationCategories.EconomicsCalculation,
            workRelevance = LizergyCalculationCategories.EconomicsCalculation,
            materialValueAddedTax = ValueAddedTax.zero,
            workValueAddedTax = ValueAddedTax.zero,
          ) {
            details = "wenn von $sellingCompanyName beauftragt"
          }
        }
      }

      // 1.x Überspannungsschutz DC
      if (facilityConfiguration.ueberSpannungsSchutzCount > 0) {
        section1Photovoltaik {
          itemWithWork(
            headline = configuration.ueberspannungsSchutzType,
            priceForMaterialAndWork = if (assemblyConfiguration.externerBlitzschutzVorhanden) priceList.ueberSpannungsSchutzaWennExternerBlitzschutzVorhanden else priceList.ueberSpannungsSchutz,
            amount = facilityConfiguration.ueberSpannungsSchutzCount,
            materialRelevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.ElectricityMaterial,
            workRelevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.ElectricityWork,
            visibility = Public,
            materialValueAddedTax = ValueAddedTax.zero,
            workValueAddedTax = ValueAddedTax.zero,
          )
        }
      }

      // 1.x Satellitenantenne ummontieren
      if (assemblyConfiguration.moveSatelliteDish) {
        section1Photovoltaik {
          itemWithWork(
            headline = "Satellitenantenne ummontieren",
            priceForMaterialAndWork = priceList.moveSatelliteDish,
            visibility = Public,
            materialRelevance = LizergyCalculationCategories.EconomicsCalculation,
            workRelevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.ElectricityWork,
            materialValueAddedTax = ValueAddedTax.nineteenPercent,
            workValueAddedTax = ValueAddedTax.nineteenPercent,
          )
        }
      }

      // 1.x Antenne entfernen
      if (assemblyConfiguration.removeAntenna) {
        section1Photovoltaik {
          itemWithWork(
            headline = "Antenne entfernen",
            priceForMaterialAndWork = priceList.removeAntenna,
            materialRelevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.Assembly,
            workRelevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.Assembly,
            visibility = Public,
            materialValueAddedTax = ValueAddedTax.nineteenPercent,
            workValueAddedTax = ValueAddedTax.nineteenPercent,
          )
        }
      }

      // 1.x Überspannungsschutz AC
      when (electricityWorkConfiguration.ueberspannungsschutzAC) {
        UeberspannungsschutzACType.Standard -> section1Photovoltaik {
          itemWithWork(
            headline = "Überspannungsschutz AC",
            priceForMaterialAndWork = priceList.ueberSpannungsSchutzAC,
            materialRelevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.ElectricityMaterial,
            workRelevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.ElectricityWork,
            visibility = Public,
            materialValueAddedTax = ValueAddedTax.zero,
            workValueAddedTax = ValueAddedTax.zero,
          )
        }

        UeberspannungsschutzACType.MitAeusseremBlitzschutz -> section1Photovoltaik {
          itemWithWork(
            headline = "Überspannungsschutz AC mit Berücksichtigung des äußeren Blitzschutzes",
            priceForMaterialAndWork = priceList.ueberSpannungsSchutzACMitBlitzschutz,
            materialRelevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.ElectricityMaterial,
            workRelevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.ElectricityWork,
            visibility = Public,
            materialValueAddedTax = ValueAddedTax.zero,
            workValueAddedTax = ValueAddedTax.zero,
          )
        }

        UeberspannungsschutzACType.Bauseits -> {
          // No quote position - customer provides their own AC surge protection
        }
      }

      // 1.x Elektrische Inbetriebnahme der Photovoltaikanlage
      section1Photovoltaik {
        itemWithWork(
          headline = "Elektrische Inbetriebnahme der Photovoltaikanlage",
          priceForMaterialAndWork = priceList.startingUpPrice,
          materialRelevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.ElectricityMaterial,
          workRelevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.ElectricityWork,
          visibility = Public,
          materialValueAddedTax = ValueAddedTax.zero,
          workValueAddedTax = ValueAddedTax.zero,
        ) {
          details =
            "Elektrische Inbetriebnahme, Verlegung der Leitungen, Erdung der PV-Anlage, sowie Installation und Inbetriebnahme des Wechselrichters durch einen Elektromeister der $sellingCompanyName GmbH, Anmeldung beim Netzbetreiber und technische Detailklärung werden bei uns von einem Ingenieur durchgeführt. Wir gehen davon aus, dass der Zählerschrank die Leistung aufnehmen kann und genügend Platz für die notwendige Elektrik bietet."
        }
      }

      // 1.x Integration PV Anlage in äußeren Blitzschutz
      if (assemblyConfiguration.externerBlitzschutzVorhanden) {
        section1Photovoltaik {
          item(
            headline = "Integration PV Anlage in äußeren Blitzschutz",
            priceForOneElement = priceList.blitzschutzIntegrieren,
            relevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.ElectricityMaterial,
            visibility = Public,
            valueAddedTax = ValueAddedTax.zero,
          )
        }
      }

      // 1.x Online-Überwachung PV-Anlage
      priceList.monitoringPrice(totalPowerRating, batteryConfiguration?.battery != null).let { monitoringPrice ->
        section1Photovoltaik {
          optionalItem(
            configuredOptionality = facilityConfiguration.onlineMonitoring,
            headline = "Wartungsvertrag PV-Anlage",
            priceForOneElement = monitoringPrice,
            relevance = LizergyCalculationCategories.EconomicsCalculation,
            visibility = Public,
            valueAddedTax = ValueAddedTax.nineteenPercent,
          ) {
            details = buildString {
              appendLine("Anlagenüberwachung durch die Firma $monitoringCompanyName für die Laufzeit eines Jahres.")
              appendLine("Nach Fertigstellung der Anlage übersenden wir Ihnen einen Wartungsvertrag. Für die Verlängerung kann dieser unterschrieben zurückgesendet werden.")
              appendLine("Inhalt des Wartungsvertrags:")
              appendLine("Ihre PV-Anlage wird regelmäßig online überwacht. Wir prüfen die Anlageperformance auf Störungen und Fehler.")
            }
          }
        }
      }


      // 2 Batterie
      // 2.1 Batterie
      if (batteryConfiguration != null && batteryConfiguration.battery != null) {
        section2Battery {
          section(
            headline = "Batteriekomplettsystem mit ${batteryConfiguration.totalStorage.formatKiloWattHour(CurrentI18nConfiguration)} Speicherkapazität",
            visibility = Public,
          ) {

            details = buildString {
              val description = buildString {
                append(batteryConfiguration.battery.description)
                append(" ")
                append("(${batteryConfiguration.battery.numberOfBatteryModules} Batteriemodul${if (batteryConfiguration.battery.numberOfBatteryModules > 0) "e" else ""})")
                when (batteryConfiguration.inverterType.manufacturer) {
                  Manufacturer.SMA -> {}
                  Manufacturer.IBC -> {
                    append(" ")
                    append("des deutschen Lieferanten und Garantiegebers IBC mit 12 Jahren Herstellergarantie.")
                  }

                  else -> throw IllegalStateException("Unknown manufacturer for inverterType: ${batteryConfiguration.inverterType.manufacturer}")
                }
              }
              appendLine("- $description")
              if (batteryConfiguration.extension != null) appendLine("- ${batteryConfiguration.extension.description} (${batteryConfiguration.extension.numberOfBatteryModules} Batteriemodul${if (batteryConfiguration.battery.numberOfBatteryModules > 0) "e" else ""})")
              appendLine("- Inklusive Kleinteile und Montage")
              appendLine()
              if (batteryConfiguration.inverterType !is HybridInverter) append("- mit ${batteryConfiguration.inverterType.formatForQuote("em")} von ${batteryConfiguration.inverterType.manufacturer.name}")
              appendLine()
              appendLine("Nur in Kombination möglich mit:")
              when (batteryConfiguration.inverterType.manufacturer) {
                Manufacturer.SMA -> appendLine("- Energiemanagementsystem Sunny Home Manager 2.0")
                Manufacturer.IBC -> appendLine("- Energiemanagementsystem IBC HomeOne Hub BT-RE1")
                else -> throw IllegalStateException("Unknown manufacturer for inverterType: ${batteryConfiguration.inverterType.manufacturer}")
              }
              if (batteryConfiguration.inverterType is HybridInverter) appendLine("- Hybrid-Wechselrichter ${batteryConfiguration.inverterType.getShortType()}")
            }

            itemWithWork(
              headline = batteryConfiguration.battery.description,
              priceForMaterialAndWork = priceList[batteryConfiguration.battery],
              materialRelevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.BatteryStorage,
              workRelevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.ElectricityWork,
              visibility = Internal,
              materialValueAddedTax = ValueAddedTax.zero,
              workValueAddedTax = ValueAddedTax.zero,
            )

            item(
              headline = "Kleinteile und Zubehör Batterie",
              priceForOneElement = priceList.getExtraPrice(batteryConfiguration.battery),
              amount = 1.0,
              relevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.BatteryStorage,
              visibility = Internal,
              valueAddedTax = ValueAddedTax.zero,
            )

            if (batteryConfiguration.extension != null) {
              itemWithWork(
                headline = batteryConfiguration.extension.description,
                priceForMaterialAndWork = priceList[batteryConfiguration.extension],
                materialRelevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.BatteryStorage,
                workRelevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.ElectricityWork,
                visibility = Internal,
                materialValueAddedTax = ValueAddedTax.zero,
                workValueAddedTax = ValueAddedTax.zero,
              )

              item(
                headline = "Kleinteile und Zubehör Batterie Erweiterung",
                priceForOneElement = priceList.getExtraPrice(batteryConfiguration.extension),
                amount = 1.0,
                relevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.BatteryStorage,
                visibility = Internal,
                valueAddedTax = ValueAddedTax.zero,
              )
            }

            if (batteryConfiguration.inverterType !is HybridInverter) {
              item(
                headline = batteryConfiguration.inverterType.description,
                priceForOneElement = priceList[batteryConfiguration.inverterType],
                amount = 1.0,
                relevance = LizergyCalculationCategories.EconomicsCalculation,
                visibility = Internal,
                valueAddedTax = ValueAddedTax.zero,
              )
            }

            /**
             * The electrician might need to drive and additional time if the system has a battery installation
             */
            item(
              headline = "Fahrtkosten Elektriker Batterieinbetriebnahme (km)",
              priceForOneElement = priceList.perKilometerTravelled,
              amount = toAndBackTravelDistance,
              relevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.ElectricityWork,
              visibility = Internal,
              valueAddedTax = ValueAddedTax.zero,
            ) {
              amount = Amount(toAndBackTravelDistance, AmountUnit.KiloMeters)
            }
          }
        }
      }


      // 3 Zusätzliche Leistungen
      section3Additional {
        optionalItemWithWork(
          configuredOptionality = facilityConfiguration.integrateInverterIntoNetwork,
          headline = "Integration Wechselrichter in Hausnetzwerk",
          priceForMaterialAndWork = priceList.integrateInverterIntoNetwork,
          materialRelevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.ElectricityMaterial,
          workRelevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.ElectricityWork,
          visibility = Public,
          materialValueAddedTax = ValueAddedTax.zero,
          workValueAddedTax = ValueAddedTax.zero,
        )
      }

      if (batteryConfiguration?.inverterType != null || electricityWorkConfiguration.sunnyHomeManager(
          selectedBattery = batteryConfiguration,
          waermepumpeSelected = facilityConfiguration.waermepumpenanbindung,
          intelligentWallboxSelected = wallboxSelection.isSmartWallBoxSelected,
          heizstabSelected = facilityConfiguration.heaterRod != null,
        )
      ) {
        val manufacturer = batteryConfiguration?.inverterType?.manufacturer ?: inverterSelection.firstOrNull()?.element?.manufacturer ?: Manufacturer.SMA
        val title = when (manufacturer) {
          Manufacturer.SMA -> "Sunny Home Manager 2.0"
          Manufacturer.IBC -> "IBC HomeOne Hub BT-RE1"
          else -> throw NotImplementedError("Unknown manufacturer for Home Manager: $manufacturer")
        }
        val details = when (manufacturer) {
          Manufacturer.SMA -> """
                Energiemanagementsystem bspw. notwendig für Einbindung von:
                - PV-optimierter E-Ladestation
                - Speicher
                - Heizstab PV-optimiert betreiben
                - Wärmepumpen PV-optimiert betrieben
                - weitere Verbraucher intelligent ansteuern
              """.trimIndent()

          Manufacturer.IBC -> """
                Energiemanagementsystem bspw. notwendig für Einbindung von:
                - PV-optimierter E-Ladestation
                - Speicher
                - Heizstab PV-optimiert betreiben
              """.trimIndent()

          else -> throw NotImplementedError("Unknown manufacturer for Home Manager: $manufacturer")
        }
        section3Additional {
          section(
            headline = "$title Energiemanagementsystem",
            visibility = Public,
          ) {
            this.details = details

            itemWithWork(
              headline = "$title Energiemanagementsystem",
              priceForMaterialAndWork = priceList.homeManagers[manufacturer],
              materialRelevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.ElectricityMaterial,
              workRelevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.ElectricityWork,
              visibility = Internal,
              materialValueAddedTax = ValueAddedTax.zero,
              workValueAddedTax = ValueAddedTax.zero,
            )

            item(
              headline = "$title Zubehör",
              priceForOneElement = priceList.homeManagerExtras[manufacturer],
              relevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.ElectricityWork,
              visibility = Internal,
              valueAddedTax = ValueAddedTax.zero,
            )
          }
        }
      }

      facilityConfiguration.heaterRod?.let {
        section3Additional {
          itemWithWork(
            headline = it.description,
            priceForMaterialAndWork = priceList[it],
            materialRelevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.ElectricityMaterial,
            workRelevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.ElectricityWork,
            visibility = Public,
            materialValueAddedTax = ValueAddedTax.nineteenPercent,
            workValueAddedTax = ValueAddedTax.nineteenPercent,
          ) {
            details = """
                  stufenlos Überschussstrom als Wärme nutzen.
                  Prüfung der mechanischen Passung mit und Einbau in Warmwasserspeicher erfolgt durch einen bauseits organisierten Sanitär. Kosten für Sanitärbetrieb sind nicht enthalten.
                  Datenblatt kann im Bedarfsfall zur Verfügung gestellt werden. Nur in Kombination mit SHM 2.0 möglich.
                """.trimIndent()
          }
        }
      }

      if (facilityConfiguration.waermepumpenanbindung) {
        section3Additional {
          itemWithWork(
            headline = "Wärmepumpenanbindung",
            priceForMaterialAndWork = priceList.waermepumpenanbindung,
            materialRelevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.ElectricityMaterial,
            workRelevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.ElectricityMaterial,
            visibility = Public,
            materialValueAddedTax = ValueAddedTax.nineteenPercent,
            workValueAddedTax = ValueAddedTax.nineteenPercent,
          ) {
            details = """
                  Die Wärmepumpe muss mindestens über den SG-Ready Standard verfügen. Idealerweise wird eine zu SMA kompatible Wärmepumpe gewählt. Je nach Kompatibilität kann die Anbindung dann auch günstiger realisiert werden.
                  Der mögliche Eigenverbrauchsoptimierungsgrad hängt ebenfalls entscheidend davon ab. Die Kompatibilitätsliste kann auf Anfrage ausgehändigt werden. Nur in Kombination mit einem Sunny Home Manager 2.0 möglich. Für die Umsetzung ist ein bauseits organisierter Termin mit dem zuständigen Sanitätsbetrieb notwendig. Die Kosten für den Sanitärbetrieb sind nicht enthalten.
                """.trimIndent()
          }
        }
      }

      electricityWorkConfiguration.independenceManager?.let { independenceManagerType ->
        section3Additional {
          itemWithWork(
            headline = independenceManagerType.description,
            priceForMaterialAndWork = priceList.independenceManagerTypePrices[independenceManagerType],
            materialRelevance = LizergyCalculationCategories.ElectricityMaterial,
            workRelevance = LizergyCalculationCategories.ElectricityWork,
            visibility = Public,
            materialValueAddedTax = ValueAddedTax.zero,
            workValueAddedTax = ValueAddedTax.zero,
          ) {
            details = independenceManagerType.details
          }
        }
      }

      if (electricityWorkConfiguration.smaEnergyMeter) {
        section3Additional {
          itemWithWork(
            headline = "SMA Energy Meter",
            priceForMaterialAndWork = priceList.smaEnergyMeter,
            materialRelevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.ElectricityMaterial,
            workRelevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.ElectricityWork,
            visibility = Public,
            materialValueAddedTax = ValueAddedTax.zero,
            workValueAddedTax = ValueAddedTax.zero,
          )
        }
      }

      if (facilityConfiguration.erdspiessSetzen) {
        section3Additional {
          itemWithWork(
            headline = "Erdspieß setzen",
            priceForMaterialAndWork = priceList.erdspiessSetzen,
            materialRelevance = LizergyCalculationCategories.ElectricityWork,
            workRelevance = LizergyCalculationCategories.ElectricityWork,
            visibility = Public,
            materialValueAddedTax = ValueAddedTax.zero,
            workValueAddedTax = ValueAddedTax.zero,
          )
        }
      }

      if (electricityWorkConfiguration.slsNachruesten) {
        section3Additional {
          itemWithWork(
            headline = "SLS nachrüsten",
            priceForMaterialAndWork = priceList.slsNachruesten,
            materialRelevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.ElectricityWork,
            workRelevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.ElectricityWork,
            visibility = Public,
            materialValueAddedTax = ValueAddedTax.zero,
            workValueAddedTax = ValueAddedTax.zero,
          )
        }
      }

      if (electricityWorkConfiguration.neuerZaehlerschrank) {
        section3Additional {
          itemWithWork(
            headline = "Neuer Zählerschrank EFH",
            priceForMaterialAndWork = priceList.neuerZaehlerschrank,
            materialRelevance = LizergyCalculationCategories.ElectricityWork,
            workRelevance = LizergyCalculationCategories.ElectricityWork,
            visibility = Public,
            materialValueAddedTax = ValueAddedTax.zero,
            workValueAddedTax = ValueAddedTax.zero,
          ) {
            details = "Grober Schätzpreis, Abrechnung nach tatsächlichem Aufwand"
          }
        }
      }

      when (electricityWorkConfiguration.enwg14aIntegration) {
        EnWG14aIntegrationType.None -> Unit
        EnWG14aIntegrationType.InZaehlerschrank -> section3Additional {
          itemWithWork(
            headline = EnWG14aIntegrationType.InZaehlerschrank.quoteHeadline.requireNotNull { "InZaehlerschrank must have a quote headline" },
            priceForMaterialAndWork = priceList.enwg14aIntegrationInZaehlerschrank,
            materialRelevance = LizergyCalculationCategories.ElectricityWork,
            workRelevance = LizergyCalculationCategories.ElectricityWork,
            visibility = Public,
            materialValueAddedTax = ValueAddedTax.zero,
            workValueAddedTax = ValueAddedTax.zero,
          ) {
            details = "Fachgerechter Umbau und Erweiterung des bestehenden Zählerschranks zur Integration der Steuertechnik gemäß TAR nach VDE AR N 4100."
          }
        }

        EnWG14aIntegrationType.InAufputzgehaeuse -> section3Additional {
          itemWithWork(
            headline = EnWG14aIntegrationType.InAufputzgehaeuse.quoteHeadline.requireNotNull { "InAufputzgehaeuse must have a quote headline" },
            priceForMaterialAndWork = priceList.enwg14aIntegrationInAufputzgehaeuse,
            materialRelevance = LizergyCalculationCategories.ElectricityWork,
            workRelevance = LizergyCalculationCategories.ElectricityWork,
            visibility = Public,
            materialValueAddedTax = ValueAddedTax.zero,
            workValueAddedTax = ValueAddedTax.zero,
          ) {
            details = "Fachgerechter Umbau und Erweiterung des bestehenden Zählerschranks zur Integration der Steuertechnik gemäß TAR nach VDE AR N 4100."
          }
        }
      }

      if (electricityWorkConfiguration.zusammenZuLegendeZaehlerNummern.isNotEmpty()) {
        section3Additional {
          itemWithWork(
            headline = "Zähler zusammenlegen",
            priceForMaterialAndWork = priceList.zaehlerschrankZusammenlegen,
            amount = electricityWorkConfiguration.zusammenZuLegendeZaehlerNummern.size,
            materialRelevance = LizergyCalculationCategories.ElectricityWork,
            workRelevance = LizergyCalculationCategories.ElectricityWork,
            visibility = Public,
            materialValueAddedTax = ValueAddedTax.nineteenPercent,
            workValueAddedTax = ValueAddedTax.nineteenPercent,
          )
        }
      }

      if (electricityWorkConfiguration.einbauUnterverteiler) {
        section3Additional {
          itemWithWork(
            headline = "Einbau Unterverteiler",
            priceForMaterialAndWork = priceList.einbauUnterverteiler,
            materialRelevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.ElectricityWork,
            workRelevance = LizergyCalculationCategories.EconomicsCalculation + LizergyCalculationCategories.ElectricityWork,
            visibility = Public,
            materialValueAddedTax = ValueAddedTax.zero,
            workValueAddedTax = ValueAddedTax.zero,
          )
        }
      }

      if (electricityWorkConfiguration.einbauDigitalerZaehler > 0) {
        section3Additional {
          itemWithWork(
            headline = "Einbau digitaler Zähler",
            priceForMaterialAndWork = priceList.einbauDigitalerZaehlerPrice,
            amount = electricityWorkConfiguration.einbauDigitalerZaehler,
            materialRelevance = LizergyCalculationCategories.ElectricityWork,
            workRelevance = LizergyCalculationCategories.ElectricityWork,
            visibility = Public,
            materialValueAddedTax = ValueAddedTax.nineteenPercent,
            workValueAddedTax = ValueAddedTax.nineteenPercent,
          )
        }
      }

      val electricityWorkEffort = electricityWorkConfiguration.electricityWorkEffort
      section3Additional {
        item(
          headline = electricityWorkEffort.headline,
          priceForOneElement = electricityWorkEffort.priceForOneElement,
          amount = electricityWorkEffort.amount,
          relevance = electricityWorkEffort.relevance,
          visibility = Public,
          valueAddedTax = electricityWorkEffort.valueAddedTax,
        ) {
          details = electricityWorkEffort.details
        }
      }

      wallboxSelection.entries.fastForEach { (wallboxType, amount) ->
        section3Additional {
          item(
            headline = wallboxType.description,
            priceForOneElement = priceList[wallboxType],
            amount = amount.toDouble(),
            relevance = LizergyCalculationCategories.ElectricityMaterial,
            visibility = Public,
            valueAddedTax = ValueAddedTax.nineteenPercent,
          ) {
            details = buildString {
              wallboxType.details?.let {
                appendLine(it)
              }
            }
          }
        }

        section3Additional {
          itemWithWork(
            headline = "${wallboxType.description} Montage",
            amount = amount,
            priceForMaterialAndWork = priceList.wallboxInstallationPrice,
            materialRelevance = LizergyCalculationCategories.ElectricityMaterial,
            workRelevance = LizergyCalculationCategories.ElectricityWork,
            visibility = Public,
            materialValueAddedTax = ValueAddedTax.nineteenPercent,
            workValueAddedTax = ValueAddedTax.nineteenPercent,
          ) {
            details = buildString {
              appendLine("Montage der E-Ladestation erfolgt nach tatsächlichem Aufwand. Je nach Kabelweg kommen noch Kosten für das Kabel und die Absicherung im Zählerschrank hinzu - hierbei muss im Schnitt mit 900€ gerechnet werden - Der Zählerschrank muss den aktuellen Vorschriften entsprechen. An einen nicht den Normen entsprechenden Zählerschrank darf keine Wallbox angeschlossen werden.")

              if (wallboxType.requiresInquiry) {
                appendLine("Achtung Netzvoranfrage ist wichtig, diese kann abgelehnt werden. Lehnt der Netzbetreiber einen Netzanschluss ab, sind wir berechtigt, vom Kaufvertrag zurückzutreten.")
              }
            }
          }
        }
      }

      // Additional position
      additionalPositions.fastForEach { additionalPosition ->
        section3Additional {
          item(
            headline = additionalPosition.headline,
            priceForOneElement = additionalPosition.priceForOneElement,
            amount = additionalPosition.amount,
            relevance = additionalPosition.relevance,
            visibility = Public,
            valueAddedTax = additionalPosition.valueAddedTax,
          ) {
            details = additionalPosition.details
          }
        }
      }


      return build(discountPercentage)
    }
  }
}

